<script setup lang="ts" generic="T">
import {computed} from 'vue'
import {useI18n} from 'vue-i18n'
import type {Maybe} from '../types'
import {useNewUiEnabled} from '@/composable/useNewUiEnabled'

type Emits = {
  change: [value: any]
}

interface Props {
  compareCallback?: (item: any, value: any) => boolean
  customStyle?: string
  disabled?: boolean
  disabledCallback?: (item: any) => boolean
  items: Maybe<readonly T[]>
  selectPlaceHolder?: Maybe<string>
  value?: Maybe<any>
  valueFormatter?: (item: any) => string
}

const {t} = useI18n()

const emits = defineEmits<Emits>()

const props = withDefaults(defineProps<Props>(), {
  compareCallback: (item: any, value: any) => item === value,
  disabled: false,
  disabledCallback: (_: any) => false,
  selectPlaceHolder: null,
  valueFormatter: (item: any) => String(item),
})

const isNewUiEnabled = useNewUiEnabled()

const isEmpty = computed(() => (props.items ? props.items.length === 0 : true))
const isDisabled = computed(() => props.disabled || isEmpty.value)
const placeHolder = computed(() => (isEmpty.value ? t('select.empty') : props.selectPlaceHolder || t('select.notSet')))

function onInput(event: Event) {
  const target = event.target as HTMLSelectElement | null
  if (props.items && target) emits('change', props.items[target.value as unknown as number])
}
</script>

<template>
  <div
    :class="{
      [customStyle ?? '']: true,
      'relative w-full px-2 py-1 flex items-center overflow-hidden': true,
      'bg-brandlight hover:bg-brandlightdarker ': !isNewUiEnabled,
      'bg-new-neutral-50 group cursor-pointer flex-row-reverse justify-between rounded-new-xl container_new_ui': isNewUiEnabled,
    }"
    data-testid="selectContainer"
  >
    <label class="text-base"
      ><i
        :class="{'fa-solid fa-angle-down text-sm': true, 'text-brandmain': !isNewUiEnabled, 'text-new-neutral-700': isNewUiEnabled}"
      ></i
    ></label>
    <select
      :class="{
        'absolute w-full pl-4 flex-grow text-base bg-transparent h-full': true,
        'text-branddark': !isNewUiEnabled,
        'text-new-neutral-700': isNewUiEnabled,
        'cursor-not-allowed': isDisabled,
        'cursor-pointer group-hover:text-new-secondary-500': !isDisabled && isNewUiEnabled,
      }"
      :disabled="isDisabled"
      @input="onInput"
    >
      <option v-if="value === null || value === undefined" disabled selected>{{ placeHolder }}</option>
      <option
        v-for="(item, idx) in items"
        :key="idx"
        :disabled="disabledCallback(item)"
        :selected="compareCallback(item, value)"
        :value="idx"
      >
        {{ valueFormatter(item) }}
      </option>
    </select>
  </div>
</template>

<style scoped>
.container_new_ui:has(:focus-within) {
  @apply outline-1 outline-new-primary-500 outline outline-offset-2;
}
</style>
